import { Arr } from '@deqo/foundation';
import { GeomUtil } from '../../../platform/geom/GeomUtil';
import { Line } from '../../../platform/geom/Line';
import { toRadians } from '../../../platform/geom/Math';
import { Path } from '../../../platform/geom/Path';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
// import { DebugOverlay } from '../../canvas/DebugOverlay';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { TextAlignment, TextSettings } from '../../canvas/entities/TextConventions';
import { TextRuler } from '../../canvas/visualization/TextRuler';
import { Connector } from './Connector';
import { ConnectorCapStyle, ConnectorMode } from './ConnectorEnums';

export const ConnectorLabelTextSettings = TextSettings.from({
    fontFamily: 'Rubik',
    fontColor: Colors.text,
    fontWeight: 400,
    fontSize: 18,
    lineHeight: 1.25,
    textMargin: 0,
    fontStyle: 'normal',
    maxFontSize: null,
    textAlignH: TextAlignment.middle,
    textAlignV: TextAlignment.middle,
});

const computeLabel = (label: string, body: Path) => {
    const cp = body.pointAt(.5);
    const size = TextRuler.measureSize(label, ConnectorLabelTextSettings);
    return Rect.fromCenter(cp, size.divide(2));
};

const prepareSegment = (figure: readonly Point[], region: Polyline) => {
    const f0 = region.contains(figure[0]);
    const f1 = region.contains(figure[1]);
    if (!f0 && !f1) return [null, false] as const;
    const mirror = !!f1;
    const segment = new Line(figure[0], figure[1]);
    return [mirror ? segment.reverse() : segment, mirror] as const;
};

const offsetClipPoint = (from: Point, to: Point, offset: number) => {
    return to.add(Point.vector(from, to).normalize().multiply(offset));
};

const clipRegion = (figure: readonly Point[], region: Polyline, method?: 'intersect' | 'rangle') => {
    method = method ?? 'intersect';

    const f0 = region.contains(figure[0]);
    const f1 = region.contains(figure[1]);
    if (!f0 && !f1) return figure;

    const mirror = !!f1;
    let segment = mirror ? new Line(figure[1], figure[0]) : new Line(figure[0], figure[1]);
    segment = segment.rotate(segment.p1, toRadians(90));

    const edge = region.lines.find(x => x.intersects(segment));

    const clipPointExact = method === 'intersect'
        ? (
            edge?.intersectPoint(segment) ??
            region.nearestPointTo(segment.p1) ??
            segment.p1
        ) : (
            edge?.nearestPointTo(segment.p1) ??
            segment.p1
        );

    const clipPoint = offsetClipPoint(segment.p1, clipPointExact, 5);

    const clipped = [clipPoint, segment.p2];
    return mirror ? clipped.reverse() : clipped;
};

const clipRegionToEdge = (figure: readonly Point[], region: Polyline) => {

    const f0 = region.contains(figure[0]);
    const f1 = region.contains(figure[1]);
    if (!f0 && !f1) return figure;

    const mirror = !!f1;
    const segment = mirror ? new Line(figure[1], figure[0]) : new Line(figure[0], figure[1]);

    const edge = region.lines.find(x => x.intersects(segment));

    const clipPointExact = (
        edge?.nearestPointTo(segment.p1) ??
        segment.p1
    );

    const clipPoint = offsetClipPoint(segment.p1, clipPointExact, 5);

    const clipped = [clipPoint, segment.p2];
    return mirror ? clipped.reverse() : clipped;
};

const clipToEdge = (figure: readonly Point[], regions: Polyline[]) => {
    return regions.reduce((t, x) => clipRegionToEdge(t, x), figure);
};

const clipRegionDirect = (figure: readonly Point[], region: Polyline) => {

    const [segment, mirror] = prepareSegment(figure, region);
    if (!segment) return figure;

    const edge = region.lines.find(x => x.intersects(segment));

    const clipPointExact = (
        edge?.intersectPoint(segment) ??
        region.nearestPointTo(segment.p1) ??
        segment.p1
    );

    const clipPoint = offsetClipPoint(segment.p1, clipPointExact, 5);

    const clipped = [clipPoint, segment.p2];
    return mirror ? clipped.reverse() : clipped;
};

const clipDirect = (figure: readonly Point[], regions: Polyline[]) =>
    regions.reduce((t, x) => clipRegionDirect(t, x), figure);

const computeLineArrowCap = (side: 'start' | 'end', spine: readonly Point[], normal: Point, strokeWidth: number) => {

    const x = side === 'start' ? Arr.at(spine, 0) : Arr.at(spine, 1);
    const v = normal;

    const back = v.normalize().inverse().multiply(strokeWidth * 4);
    const neck = back;
    const end1 = back.rotate(toRadians(45));
    const end2 = back.rotate(toRadians(-45));

    const path = new Path([
        ['M', x.add(end1)],
        ['L', x],
        ['L', x.add(end2)],
    ]);

    return [path, x.add(neck)] as const;
};

const computeSolidArrowCap = (side: 'start' | 'end', spine: readonly Point[], normal: Point, strokeWidth: number) => {

    const x = side === 'start' ? Arr.at(spine, 0) : Arr.at(spine, 1);

    const back = normal.inverse().multiply(strokeWidth * 6);
    const neck = back.multiply(.6);
    const end1 = back.rotate(toRadians(30));
    const end2 = back.rotate(toRadians(-30));

    const path = new Path([
        ['M', x],
        ['L', x.add(end1)],
        ['L', x.add(neck)],
        ['L', x.add(end2)],
        ['Z']
    ]);

    return [path, x.add(neck.multiply(2))] as const;
};

const computeSpotCap = (side: 'start' | 'end', spine: readonly Point[], normal: Point, strokeWidth: number) => {

    const x = side === 'start' ? Arr.at(spine, 0) : Arr.at(spine, 1);
    const r = strokeWidth;

    const neck = normal.inverse().multiply(r * 2);

    const path = new Path([
        ['M', x.add([-r, 0])],
        ['a', new Point(r * 2, 0), r, r, 0, 1, 0],
        ['a', new Point(r * -2, 0), r, r, 0, 1, 0],
        ['Z']
    ]);

    return [path, x.add(neck)] as const;
};

const computeNoneCap = (side: 'start' | 'end', spine: readonly Point[], normal: Point, _strokeWidth: number) => {

    const x = side === 'start' ? Arr.at(spine, 0) : Arr.at(spine, 1);
    const neck = normal.inverse();

    return [null as Path, x.add(neck)] as const;
};

const computeCap = (side: 'start' | 'end', spine: readonly Point[], normal: Point, strokeWidth: number, style: number) => {
    if (style === ConnectorCapStyle.spot) return computeSpotCap(side, spine, normal, strokeWidth);
    if (style === ConnectorCapStyle.solidArrow) return computeSolidArrowCap(side, spine, normal, strokeWidth);
    if (style === ConnectorCapStyle.lineArrow) return computeLineArrowCap(side, spine, normal, strokeWidth);
    return computeNoneCap(side, spine, normal, strokeWidth);
};

const computeOverflow = (figure: readonly Point[], clipped: readonly Point[]) => {
    const pb = Path.builder();

    if (figure[0] !== clipped[0]) {
        pb.moveTo(clipped[0]);
        pb.lineTo(figure[0]);
    }
    if (figure[1] !== clipped[1]) {
        pb.moveTo(clipped[1]);
        pb.lineTo(figure[1]);
    }

    return pb.empty ? null : pb.build();
};

const computeSpine = (figure: readonly Point[], clipped: readonly Point[], body: Path) => {
    return Path.builder()
        .moveTo(figure[0])
        .lineTo(clipped[0])
        .addPath(body)
        .lineTo(clipped[1])
        .lineTo(figure[1])
        .build();
};

const computeDirectLayout = (object: Connector, fromBox?: Rect, toBox?: Rect) => {

    const mlm = object.transform(RelativeTo.Model).inverse();

    const from = !!fromBox ? Polyline.from(mlm.apply(fromBox), true) : null;
    const to = !!toBox ? Polyline.from(mlm.apply(toBox), true) : null;

    const figure = mlm.apply(object.createFigure());
    const clipped = clipDirect(figure, [from, to].filter(x => !!x));

    const startNormal = Point.vector(figure[1], figure[0]).normalize();
    const endNormal = Point.vector(figure[0], figure[1]).normalize();

    const [start, startNeck] = computeCap('start', clipped, startNormal, object.strokeWidth, object.startCap);
    const [end, endNeck] = computeCap('end', clipped, endNormal, object.strokeWidth, object.endCap);

    const body = Polyline.from(clipped).toPath(false);
    const overflow = computeOverflow(figure, clipped);
    const spine = computeSpine(figure, clipped, body);

    const sw = object.strokeWidth;
    const base = Rect.fromMany(
        [body.bounds(sw), overflow?.bounds(sw), start?.bounds(sw), end?.bounds(sw)].filter(x => !!x)
    );

    return {
        base,
        start,
        startNormal,
        end,
        endNormal,
        body,
        overflow,
        spine,
        clipped: !!from || !!to
    };

};

const computeSmoothExpansion = (clipped: readonly Point[], startNeck?: Point, endNeck?: Point) => {

    const [start, end] = clipped;
    const s2ed = start.distanceTo(end);

    const result = [] as Point[];
    // DebugOverlay.reset();

    result.push(start);
    {
        // Neck direction
        const nd = Point.vector(start, startNeck).normalize();
        const cp = start.add(nd.multiply(s2ed / 2));
        if (startNeck) {
            result.push(startNeck);
            const ncp = startNeck.add(Point.vector(start, startNeck));
            result.push(GeomUtil.furthestPointFrom(start, [cp, ncp]));
        }
        else {
            result.push(start.add(nd.multiply(20)));
            result.push(start.add(nd.multiply(s2ed / 2)));
        }
    }
    {
        // Neck direction
        const nd = Point.vector(end, endNeck).normalize();
        const cp = end.add(nd.multiply(s2ed / 2));
        if (endNeck) {
            const ncp = endNeck.add(Point.vector(end, endNeck));
            result.push(GeomUtil.furthestPointFrom(end, [cp, ncp]));
            result.push(endNeck);
        }
        else {
            result.push(cp);
            result.push(end.add(nd.multiply(20)));
        }
    }
    result.push(end);

    if (result.length > 4) {
        return Path.builder()
            .moveTo(result[0])
            .lineTo(result[1])
            .cubeTo(result[2], result[3], result[4])
            .lineTo(result.slice(-1))
            .build();
    }

    return Polyline.from(clipped).toPath(false);
};

const computeSmoothLayout = (object: Connector, fromBox?: Rect, toBox?: Rect) => {

    const mlm = object.transform(RelativeTo.Model).inverse();
    // DebugOverlay.use(object.transform(RelativeTo.Model));

    const from = !!fromBox ? Polyline.from(mlm.apply(fromBox), true) : null;
    const to = !!toBox ? Polyline.from(mlm.apply(toBox), true) : null;

    const fromRotation = !!fromBox ? 0 : object.fromRotation;
    const toRotation = !!toBox ? 0 : object.toRotation;

    const figure = mlm.apply(object.createFigure());
    const clipped = clipToEdge(figure, [from, to].filter(x => !!x));

    const startNormal = Point
        .coalesce([
            Point.vector(clipped[0], figure[0]).normalize(),
            Point.vector(figure[1], figure[0]).projectToXOrY().normalize(),
            Point.x,
        ])
        .rotate(fromRotation);

    const endNormal = Point
        .coalesce([
            Point.vector(clipped[1], figure[1]).normalize(),
            Point.vector(figure[0], figure[1]).projectToXOrY().normalize(),
            Point.x,
        ])
        .rotate(toRotation);

    const [start, startNeck] = computeCap('start', clipped, startNormal, object.strokeWidth, object.startCap);
    const [end, endNeck] = computeCap('end', clipped, endNormal, object.strokeWidth, object.endCap);

    const body = computeSmoothExpansion(clipped, startNeck, endNeck);
    const overflow = computeOverflow(figure, clipped);
    const spine = computeSpine(figure, clipped, body);

    const base = Rect.fromMany(
        [body.bounds(object.strokeWidth),
        overflow?.bounds(),
        start?.bounds(object.strokeWidth),
        end?.bounds(object.strokeWidth)]
            .filter(x => !!x)
    );

    return {
        base,
        start,
        startNormal,
        end,
        endNormal,
        body,
        overflow,
        spine,
        clipped: !!from || !!to
    };
};

const computeManhattanBreak = (a: Point, b: Point) => {
    const m = new Line(a, b).mid;
    const v = Point.vector(a, b);
    if (v.xish()) {
        return [
            new Point(m.x, a.y),
            new Point(m.x, b.y),
        ];
    }
    else {
        return [
            new Point(a.x, m.y),
            new Point(b.x, m.y),
        ];
    }
};

const computeManhattanExpansion = (clipped: readonly Point[], startNeck: Point, endNeck?: Point) => {

    // DebugOverlay.reset();

    const [start, end] = clipped;

    const startNeckDirection = Point.vector(start, startNeck).normalize();
    const endNeckDirection = Point.vector(end, endNeck).normalize();

    const startRay = Line.ray(start, startNeckDirection);
    const endRay = Line.ray(end, endNeckDirection);

    const mid = startRay.intersectPoint(endRay) ?? new Line(start, end).mid;

    const result = [] as Point[];

    result.push(start);
    result.push(startNeck);

    const m1 = startNeckDirection.xish()
        ? new Point(mid.x, startNeck.y)
        : new Point(startNeck.x, mid.y);

    const m2 = endNeckDirection.xish()
        ? new Point(mid.x, endNeck.y)
        : new Point(endNeck.x, mid.y);

    // If either m1 or m2 are further away from their respective ends, we need to do another zig-zag
    if (startNeck.distanceTo(start) > m1.distanceTo(start) || endNeck.distanceTo(end) > m2.distanceTo(end)) {
        result.push(
            ...computeManhattanBreak(startNeck, endNeck),
        );
    }
    else {
        result.push(m1);
        result.push(m2);
    }

    result.push(endNeck);
    result.push(end);

    return Path.builder()
        .moveTo(result[0])
        .lineTo(result.slice(1))
        .build();
    // .toRounded('cubic', 10);
};

const computeManhattanLayout = (object: Connector, fromBox?: Rect, toBox?: Rect) => {

    const mlm = object.transform(RelativeTo.Model).inverse();
    // DebugOverlay.use(object.transform(RelativeTo.Model));

    const from = !!fromBox ? Polyline.from(mlm.apply(fromBox), true) : null;
    const to = !!toBox ? Polyline.from(mlm.apply(toBox), true) : null;

    const fromRotation = !!fromBox ? 0 : object.fromRotation;
    const toRotation = !!toBox ? 0 : object.toRotation;

    const figure = mlm.apply(object.createFigure());
    const clipped = clipToEdge(figure, [from, to].filter(x => !!x));

    const startNormal = Point
        .coalesce([
            Point.vector(clipped[0], figure[0]).normalize(),
            Point.vector(figure[1], figure[0]).projectToXOrY().normalize(),
            Point.x,
        ])
        .rotate(fromRotation);

    const endNormal = Point
        .coalesce([
            Point.vector(clipped[1], figure[1]).normalize(),
            Point.vector(figure[0], figure[1]).projectToXOrY().normalize(),
            Point.x,
        ])
        .rotate(toRotation);

    const [start, startNeck] = computeCap('start', clipped, startNormal, object.strokeWidth, object.startCap);
    const [end, endNeck] = computeCap('end', clipped, endNormal, object.strokeWidth, object.endCap);

    const body = computeManhattanExpansion(clipped, startNeck, endNeck);
    const overflow = computeOverflow(figure, clipped);
    const spine = computeSpine(figure, clipped, body);

    const base = Rect.fromMany(
        [body.bounds(object.strokeWidth),
        overflow?.bounds(),
        start?.bounds(object.strokeWidth),
        end?.bounds(object.strokeWidth)]
            .filter(x => !!x)
    );

    return {
        base,
        start,
        startNormal,
        end,
        endNormal,
        body,
        overflow,
        spine,
        clipped: !!from || !!to
    };
};

export const ConnectorLayout = (object: Connector) => {

    const attached = object.relations('attachee', Physical);
    const from = attached.find(x => x.id === object.fromId)?.bounds(RelativeTo.Model);
    const to = attached.find(x => x.id === object.toId)?.bounds(RelativeTo.Model);

    const lineLayout =
        ((object.mode === ConnectorMode.smooth) ? computeSmoothLayout(object, from, to) : null) ??
        ((object.mode === ConnectorMode.manhattan) ? computeManhattanLayout(object, from, to) : null) ??
        computeDirectLayout(object, from, to);

    const label = computeLabel(object.label, lineLayout.body);

    return { ...lineLayout, label };
};

export type ConnectorLayout = ReturnType<typeof ConnectorLayout>;
